import { AutodeskIntegrationModel, CameraImageModel, CameraModel, ProjectModel } from 'api/models';
import { IconButton } from 'library/buttons';
import { ChevronDownIcon } from 'library/icons';
import BlurBackgroundImage from 'modules/dashboard/components/ui/BlurBackgroundImage';
import OverlayLoader from 'modules/dashboard/components/ui/OverlayLoader';
import EventBlocker from 'modules/shared/components/helpers/EventBlocker';
import useLoading from 'modules/shared/hooks/useLoading';
import { formatDatetime } from 'modules/shared/utils/date-utils';
import { FC, useMemo, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import AutodeskImageSlider from '../AutodeskImageSlider/AutodeskImageSlider';
import { AutoDeskIntegrationTypeEnum } from 'api/enums';
import clsx from 'clsx';

interface AutodeskCameraDetailViewerProps {
    camera: CameraModel;
    project: ProjectModel;
    integration: AutodeskIntegrationModel;
    images: CameraImageModel[];
    selectedImage: CameraImageModel;
    onSelectedImageChange: (image: CameraImageModel) => void;
    selectedDate: string;
    onSelectedDateChange: (date: string) => void;
    startDate: string;
    endDate: string;
    isLoading: boolean;
}
const AutodeskCameraDetailViewer: FC<AutodeskCameraDetailViewerProps> = (props) => {
    const {
        camera,
        project,
        integration,
        images,
        selectedImage,
        onSelectedImageChange,
        selectedDate,
        onSelectedDateChange,
        startDate,
        endDate,
        isLoading,
    } = props;

    const [showImageSlider, setShowImageSlider] = useState<boolean>(false);

    const imageUrl = useMemo(() => selectedImage.url4k, [selectedImage]);

    const [imgLoading, setImgLoading] = useLoading(imageUrl);

    const toggleImageSlider = () => {
        setShowImageSlider((prev) => !prev);
    };

    const isSliderEnabled = [AutoDeskIntegrationTypeEnum.ALL_IMAGES, AutoDeskIntegrationTypeEnum.DAY_IMAGES].includes(
        integration.type
    );

    const hasBottomContent = Boolean(
        (integration.options.showTimestamp && selectedImage.datetime) ||
            integration.options.customText ||
            (integration.options.showCameraName && camera.name) ||
            (integration.options.showProjectName && project.name)
    );

    const shouldShowBlurBackground = imageUrl && (!integration.options.showZoomed || hasBottomContent);

    const imageContent = (
        <div className="flex items-center justify-center h-screen w-full overflow-hidden">
            <img
                src={imageUrl}
                alt=""
                onLoad={() => setImgLoading(false)}
                className={clsx(
                    'transition-all duration-300 ease-in-out',
                    integration.options.showZoomed
                        ? 'w-full h-full object-cover' // Zoomed: fill entire container
                        : 'max-w-full max-h-full object-contain' // Not zoomed: maintain aspect ratio
                )}
            />
        </div>
    );

    return (
        <div className="relative overflow-hidden">
            <div className="relative">
                {shouldShowBlurBackground ? (
                    <BlurBackgroundImage src={imageUrl}>{imageContent}</BlurBackgroundImage>
                ) : (
                    <div className="relative h-full w-full bg-black">{imageContent}</div>
                )}

                <OverlayLoader show={imgLoading} />

                {!imageUrl && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background-color text-grayscale-500 font-medium z-[2]">
                        No image on this date
                    </div>
                )}
            </div>

            <div
                className={twMerge(
                    'absolute left-3 right-3 bottom-3 sm:left-4 sm:right-4 sm:bottom-4 md:left-5 md:right-5 md:bottom-5 flex items-end text-white font-semibold -translate-y-[var(--image-slider-height)] transition-transform duration-[350ms]',
                    !showImageSlider ? 'translate-y-0' : ''
                )}
            >
                {isSliderEnabled && (
                    <EventBlocker stopPropagation>
                        <IconButton size="xs" color="secondary" onClick={toggleImageSlider}>
                            <ChevronDownIcon
                                width={10}
                                height={7}
                                className={twJoin(
                                    'text-grayscale-600 transition-transform duration-[350ms]',
                                    showImageSlider ? 'rotate-0' : 'rotate-180'
                                )}
                            />
                        </IconButton>
                    </EventBlocker>
                )}
                <div
                    className="ml-auto text-right max-w-sm text-xs sm:text-sm"
                    style={{ textShadow: '0px -1px #000000' }}
                >
                    {integration.options.showTimestamp && (
                        <div>{formatDatetime(selectedImage.datetime, 'h:mm a [·] DD MMM, YYYY')}</div>
                    )}

                    {integration.options.customText && <div className="mt-1">{integration.options.customText}</div>}

                    {integration.options.showCameraName && <div className="mt-1">{camera.name}</div>}

                    {integration.options.showProjectName && <div className="mt-1">{project.name}</div>}
                </div>
            </div>

            {isSliderEnabled && (
                <>
                    <AutodeskImageSlider
                        show={showImageSlider}
                        onClose={() => setShowImageSlider(false)}
                        images={images}
                        selectedImage={selectedImage}
                        onSelectedImageChange={onSelectedImageChange}
                        selectedDate={selectedDate}
                        onSelectedDateChange={onSelectedDateChange}
                        startDate={startDate}
                        endDate={endDate}
                        isLoading={isLoading}
                        showDatePicker={integration.type === AutoDeskIntegrationTypeEnum.ALL_IMAGES}
                    />
                </>
            )}
        </div>
    );
};

export default AutodeskCameraDetailViewer;
